import { type Issue } from "@/model/tasks/issue"
import type { IssueListItem } from "@/pages/Tasks/Issue/config"
import { $datetime, normalizeModuleConfiguration, type ModuleConfiguration } from "@/vf"
import { h, ref, type Ref } from "vue"
import { useI18n } from "vue-i18n"
import IssueShow from "../Issue/show.vue"
import IssueNew from "./new.vue"

export function createModuleConfiguration(apiBase: string, filterOnlyMessages?: Ref<boolean>): ModuleConfiguration {
    const i18n = useI18n()

    function getTitle(issue: Issue) {
        if (!issue.name) {
            return
        }
        let subtitle = null

        if (issue.type === "ComThread" && issue.nameProps?.name) {
            subtitle = issue.nameProps.name
        }
        if (issue.nameProps?.subtitle) {
            subtitle = issue.nameProps.subtitle
        }

        if (subtitle) {
            return [
                h("div", [i18n.t(issue.name, issue.nameProps)]),
                h(
                    "div",
                    {
                        class: "font-slab color-primary",
                    },
                    [subtitle],
                ),
            ]
        }

        return i18n.t(issue.name, issue.nameProps) ?? issue.name
    }

    return normalizeModuleConfiguration({
        routePrefix: () => "@Tasks.Customer",
        readableName: {
            plural: "@tasks:issue.issues",
            singular: "@tasks:issue.issue",
        },
        new: IssueNew,
        show: (item: Issue) => ({
            component: IssueShow,
            props: {
                id: item.id,
                viewMode: "customer",
                filterOnlyMessages: filterOnlyMessages?.value ?? false,
            },
        }),
        list: {
            fields: [
                {
                    name: "newIcon",
                    title: "",
                    type: "vnode",
                    thClass: () => "d-none d-sm-table-cell",
                    tdClass: () => "d-none d-sm-table-cell ",
                    getter: item =>
                        item["unreadMessagesCount"] > 0
                            ? h("i", { class: "fa-solid fa-envelope-open-text" })
                            : h("i", { style: "width: 1rem" }),
                },
                {
                    name: "name",
                    sortField: "a.name",
                    title: "@tasks:issue.name",
                    type: "string",
                    thClass: () => "d-none d-sm-table-cell",
                    tdClass: () => "d-none d-sm-table-cell",
                    getter: (issue: Issue) => {
                        return getTitle(issue)
                    },
                },
                {
                    name: "createdAt",
                    sortField: "a.createdAt",
                    title: "@tasks:issue.createdAt",
                    type: "datetime",
                    tdClass: () => "d-none d-sm-table-cell",
                    thClass: () => "d-none d-sm-table-cell",
                },
                {
                    name: "lastTaskAt",
                    sortField: "a.lastTaskAt",
                    title: "@tasks:issue.lastTaskAt",
                    type: "datetime",
                    tdClass: () => "d-none d-sm-table-cell",
                    thClass: () => "d-none d-sm-table-cell",
                },
                {
                    name: "taskCount",
                    sortField: "a.messageCount",
                    title: "@tasks:issue.messageCount",
                    type: "vnode",
                    thClass: () => "text-center d-none d-sm-table-cell",
                    tdClass: () => "text-center d-none d-sm-table-cell",
                    getter: (issue: IssueListItem) =>
                        h(
                            "div",
                            {
                                class: "text-center",
                                title: i18n.t("@tasks:issue.messageCountHover", {
                                    count: issue.messageCount,
                                    unread: issue.unreadMessageIds?.length ?? 0,
                                }),
                            },
                            [
                                issue.messageCount +
                                    (issue.unreadMessageIds?.length ?? 0 > 0
                                        ? " (" + issue.unreadMessageIds?.length + ")"
                                        : ""),
                            ],
                        ),
                },
                {
                    name: "taskCount",
                    sortField: "a.taskCount",
                    title: "@tasks:issue.tasksCount",
                    type: "vnode",
                    thClass: () => "text-center d-none d-sm-table-cell",
                    tdClass: () => "text-center d-none d-sm-table-cell",
                    getter: (issue: Issue) =>
                        h(
                            "div",
                            {
                                class: "text-center",
                                title: i18n.t("@tasks:issue.tasksCountHover", {
                                    count: issue.taskWithoutMessagesCount,
                                    open: issue.openTaskWithoutMessagesCount ?? 0,
                                }),
                            },
                            [
                                issue.taskWithoutMessagesCount +
                                    (issue.openTaskWithoutMessagesCount ?? 0 > 0
                                        ? " (" + issue.openTaskWithoutMessagesCount + ")"
                                        : ""),
                            ],
                        ),
                },
                {
                    name: "state",
                    sortField: "a.state",
                    title: "@tasks:issue.state",
                    type: "vnode",
                    tdClass: () => "text-right d-none d-sm-table-cell",
                    thClass: () => "text-right d-none d-sm-table-cell",
                    getter: (issue: Issue) => {
                        if (issue.state === "done") {
                            return h("span", { title: $datetime(issue.closedAt) }, [
                                i18n.t(`@tasks:issue.issue_states.done`),
                            ])
                        }

                        return i18n.t(`@tasks:issue.issue_states.${issue.state}`)
                    },
                },
                {
                    name: "mobile",
                    title: "",
                    type: "vnode",
                    tdClass: () => "d-sm-none p-3",
                    thClass: () => "d-none",
                    getter: (issue: Issue) => {
                        const output = []

                        output.push(
                            h(
                                "div",
                                h(
                                    "div",
                                    {
                                        class: "mt-1 border text-center bg-white",
                                        style: "width: 20px; height: 20px",
                                    },
                                    issue.state === "done" ? h("i", { class: "fa-solid fa-check" }) : [],
                                ),
                            ),
                        )

                        output.push(
                            h("div", { class: "flex-fill ml-3" }, [
                                h("h3", { class: "fs-1 d-inline-block" }, getTitle(issue)),
                                h("div", { class: "small" }, $datetime(issue.lastTaskAt)),
                            ]),
                        )

                        return h("div", { class: "d-flex" }, output)
                    },
                },
            ],
            orderField: "lastTaskAt",
            orderDirection: "desc",
            onClick: "show",
            requestParams: ref({
                viewMode: "customer",
                includeDone: "1",
                includeOpen: "1",
            }),
            trClass: (issue: IssueListItem) => {
                return (issue.unreadMessageIds?.length ?? 0) > 0 ? "font-weight-bold" : ""
            },
        },
        afterSaveAction: "navigate-list",
        apiBase: () => apiBase,
        mode: "in-table",
    })
}
